/**
 * Formula injection triggers in CSV/XLSX:
 * '=', '+', '-', '@', '\t', '\r'
 */
export const FORMULA_TRIGGERS = ["=", "+", "-", "@", "\t", "\r"];

/**
 * Neutralizes any value starting with formula trigger characters (=, +, -, @, \t, \r)
 * by prefixing with a single quote (').
 */
export const sanitizeExportValue = (value: any): any => {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean" || value instanceof Date) {
    return value;
  }

  const str = typeof value === "string" ? value : String(value);
  if (str.length === 0) {
    return str;
  }

  // Already prefixed
  if (str.startsWith("'")) {
    return str;
  }

  const firstChar = str[0];
  const trimmed = str.trimStart();
  const trimmedFirstChar = trimmed.length > 0 ? trimmed[0] : "";

  if (
    FORMULA_TRIGGERS.includes(firstChar) ||
    (trimmedFirstChar && FORMULA_TRIGGERS.includes(trimmedFirstChar))
  ) {
    return `'${str}`;
  }

  return value;
};

/**
 * Neutralizes any formula injection within a row (object, array, or primitive).
 */
export const sanitizeExportRow = (row: any): any => {
  if (!row || typeof row !== "object") {
    return sanitizeExportValue(row);
  }

  if (row instanceof Date) {
    return row;
  }

  if (Array.isArray(row)) {
    return row.map((cell) => sanitizeExportRow(cell));
  }

  const sanitized: Record<string, any> = {};
  for (const [key, val] of Object.entries(row)) {
    sanitized[key] = sanitizeExportRow(val);
  }
  return sanitized;
};

/**
 * Converts an array of objects to CSV format and triggers a file download
 * @param data Array of data objects
 * @param headers Map of field key to display header name (optional, will use keys if not provided)
 * @param filename Name of the file to download (without extension)
 */
export const exportToCSV = <T extends Record<string, any>>(
  data: T[],
  headers: Partial<Record<keyof T, string>> | null,
  filename: string
): void => {
  if (!data || data.length === 0) {
    console.warn("No data to export");
    return;
  }

  // Get keys from headers or first object
  const keys = headers 
    ? (Object.keys(headers) as (keyof T)[]) 
    : (Object.keys(data[0]) as (keyof T)[]);

  const headerRow = keys.map(key => headers ? headers[key] : String(key)).join(",");
  
  const rows = data.map(row => {
    return keys.map(key => {
      const value = row[key];
      const sanitized = sanitizeExportValue(value);
      // Handle strings that might contain commas or newlines
      const stringValue = sanitized === null || sanitized === undefined ? "" : String(sanitized);
      if (stringValue.includes(",") || stringValue.includes("\n") || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(",");
  });

  const csvContent = [headerRow, ...rows].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

